// src/context/TenantContext.tsx
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { getFirmBySlug, getFirmById } from "../services/firestoreService";
import type { FirmSettingsDoc } from "../types";

interface TenantContextType {
  firm: (FirmSettingsDoc & { id: string }) | null;
  loading: boolean;
  error: string | null;
}

const TenantContext = createContext<TenantContextType>({
  firm: null,
  loading: true,
  error: null,
});

export const useTenant = () => useContext(TenantContext);

const safeSessionGet = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionSet = (key: string, value: string) => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // iframe may block storage
  }
};

const resolveTenantId = (): string | null => {
  const pathSegments = window.location.pathname.split("/").filter(Boolean);
  const urlParams = new URLSearchParams(window.location.search);

  // /location/{id}/...
  if (pathSegments[0] === "location" && pathSegments[1]) return pathSegments[1];

  // query params
  const qp = urlParams.get("locationId") || urlParams.get("staff_context");
  if (qp) return qp;

  // fallback to cached tenant
  return safeSessionGet("FILERSHUB_SSO_LOC");
};

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [firm, setFirm] = useState<(FirmSettingsDoc & { id: string }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const lastTenantIdRef = useRef<string | null>(null);

  useEffect(() => {
    const applyBrandColor = (color?: string | null) => {
      if (!color) return;
      document.documentElement.style.setProperty("--brand-primary", color);
    };

    const resolveTenant = async () => {
      const tenantId = resolveTenantId();

      if (!tenantId) {
        setFirm(null);
        setError(null);
        setLoading(false);
        lastTenantIdRef.current = null;
        return;
      }

      if (lastTenantIdRef.current === tenantId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const byId = await getFirmById(tenantId);
        if (byId) {
          setFirm(byId);
          applyBrandColor(byId.brandColor);
          safeSessionSet("FILERSHUB_SSO_LOC", byId.id);
          lastTenantIdRef.current = tenantId;
          return;
        }

        const bySlug = await getFirmBySlug(tenantId);
        if (bySlug) {
          setFirm(bySlug);
          applyBrandColor(bySlug.brandColor);
          safeSessionSet("FILERSHUB_SSO_LOC", bySlug.id);
          lastTenantIdRef.current = tenantId;
          return;
        }

        setFirm(null);
        setError("Firm not found for this workspace.");
        lastTenantIdRef.current = tenantId;
      } catch (err: any) {
        console.error("[Tenant] Tenant resolution failure:", err);
        setFirm(null);
        setError(err?.message || "Tenant resolution failed.");
        lastTenantIdRef.current = tenantId;
      } finally {
        setLoading(false);
      }
    };

    const run = () => void resolveTenant();

    run();
    window.addEventListener("popstate", run);
    window.addEventListener("filershub:navigate", run as any);

    return () => {
      window.removeEventListener("popstate", run);
      window.removeEventListener("filershub:navigate", run as any);
    };
  }, []);

  return <TenantContext.Provider value={{ firm, loading, error }}>{children}</TenantContext.Provider>;
};
